import { OverlayScrollArea } from '@odo/shared-ui';
import type { AdminAppointment, AppointmentOriginEvent } from '../../../services/adminAppointments';
import { AppointmentDetailField } from './AppointmentDetailField';
import { AppointmentPatientBlock } from './AppointmentPatientBlock';
import { AppointmentDateDisplay } from './AppointmentDateDisplay';
import { AppointmentTimeDisplay } from './AppointmentTimeDisplay';
import { AppointmentReasonDisplay } from './AppointmentReasonDisplay';
import { OdoNoteCard } from './OdoNoteCard';
import { AppointmentChartNoteSection } from './AppointmentChartNote';

interface AppointmentDetailPaneProps {
    appointment: AdminAppointment;
    /** From the single-appointment GET; null until it arrives or when there is none. */
    originEvent: AppointmentOriginEvent | null;
}

/**
 * Read-only mirror of the dashboard's edit-appointment Composer (see
 * dashboard/src/components/appointments/Composer.tsx) — same field order
 * (Patient, Date, Time, Reason, ODO note, Chart note), values displayed
 * instead of editable inputs. Practice/doctor is folded into the Patient
 * field itself (see AppointmentPatientBlock) rather than a separate block.
 */
export function AppointmentDetailPane({ appointment, originEvent }: AppointmentDetailPaneProps) {
    return (
        <OverlayScrollArea
            className="flex-1 min-h-0"
            contentClassName="px-5 py-5"
            innerClassName="space-y-5"
            trackInsetPx={6}
        >
            <AppointmentDetailField label="Patient">
                <AppointmentPatientBlock appointment={appointment} />
            </AppointmentDetailField>

            <div className="grid grid-cols-2 gap-3">
                <AppointmentDetailField label="Date">
                    <AppointmentDateDisplay date={appointment.date} />
                </AppointmentDetailField>
                <AppointmentDetailField label="Time">
                    <AppointmentTimeDisplay time={appointment.time} />
                </AppointmentDetailField>
            </div>

            <AppointmentDetailField label="Reason">
                <AppointmentReasonDisplay reason={appointment.reason} />
            </AppointmentDetailField>

            {originEvent && (
                <OdoNoteCard patientId={appointment.patientId} originEvent={originEvent} />
            )}

            <AppointmentChartNoteSection appointment={appointment} />
        </OverlayScrollArea>
    );
}
